import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import * as apis from '@/apis/account';
import * as actions from '@/actions/account';
import { convertAllFilesToDataUrl } from '@/helpers/account';
import { AccountGetAccountPageRequest } from '../../actions/account/models/types';
import {
  ACCOUNT_SAVE_DELIVERY_ADDRESS_LIST_REQUEST,
  ACCOUNT_ADD_NEW_USER_REQUEST,
  ACCOUNT_DELETE_DELIVERY_ADDRESS_REQUEST,
  ACCOUNT_GET_DELIVERY_ADDRESS_LIST_REQUEST,
  ACCOUNT_GET_USER_DETAILS_REQUEST,
  ACCOUNT_UPDATE_CHANGE_USER_REQUEST,
  ACCOUNT_TRANSACTION_HISTORY_REQUEST,
  ACCOUNT_TABS_STATUS_REQUEST,
  ACCOUNT_TRANSACTION_HISTORY_SPOC_APPROVE_REQUEST,
  ACCOUNT_TRANSACTION_HISTORY_CANCEL_REQUEST,
  ACCOUNT_TRANSACTION_HISTORY_UPDATE_DELIVERY_REQUEST,
  ACCOUNT_BILLING_ACCOUNT_GET_LIST_REQUEST,
  ACCOUNT_BILLING_ACCOUNT_GET_ENROLLMENT_STATUS_REQUEST,
  ACCOUNT_BILLING_ACCOUNT_GET_SERVICE_NUMBER_LIST_REQUEST,
  BILLING_ACCOUNT_UPDATE_CREDIT_CARD_REQUEST,
  ACCOUNT_TRANSACTION_HISTORY_DETAILS_REQUEST,
  ACCOUNT_DELETE_DOCUMENT_REQUEST,
  ACCOUNT_DELETE_POLICY_REQUEST,
  ACCOUNT_DOWNLOAD_DOCUMENT_REQUEST,
  ACCOUNT_GET_ACCOUNT_PAGE_REQUEST,
  ACCOUNT_GET_POLICY_DETAILS_REQUEST,
  ACCOUNT_GET_POLICY_LSIT_REQUEST,
  ACCOUNT_GET_POLICY_TEMPLATE_REQUEST,
  ACCOUNT_GET_PROFILE_REQUEST,
  ACCOUNT_GET_PROFILE_WITHOUTDOC_REQUEST,
  ACCOUNT_SAVE_POLICY_REQUEST,
  ACCOUNT_UPDATE_PROFILE_REQUEST,
  ACCOUNT_UPLOAD_DOCUMENTS_REQUEST,
  CHANGE_BILLING_ADDRESS_REQUEST,
  ACCOUNT_GET_RESIDENTIAL_ADDRESS_LIST_REQUEST,
  ACCOUNT_EDIT_DELIVERY_ADDRESS_LIST_REQUEST,
  ACCOUNT_UPLOAD_NUMBER_PORT_DOCUMENTS_REQUEST,
  ACCOUNT_DOWNLOAD_DOCUMENT_NUMBERPORT_REQUEST,
  ACCOUNT_DELETE_DOCUMENT_NUMBERPORT_REQUEST
} from '@/actions/account/actionTypes';

export const getAuth = (state) => {
  return state.auth;
};

export const getDocumentRepo = (state) => state.account.documentRepo;
export const getPolicyTemplates = (state) => state.account.policyTemplate;

function* getAccountPage({ gridOptions }: AccountGetAccountPageRequest) {
  try {
    const {
      userInfo: { email }
    } = yield select(getAuth);
    const result = yield call(apis.getAccountPage, { ...gridOptions, email });
    yield put(actions.getAccountPage.success(result.data));
  } catch (error) {
    yield put(actions.getAccountPage.failure(error));
  }
}

function* getProfile() {
  try {
    const { currentAccount, userInfo } = yield select(getAuth);
    const result = yield call(apis.getProfile, currentAccount?.brn, userInfo?.email);
    yield put(actions.getProfile.success(result.data));
  } catch (error) {
    yield put(actions.getProfile.failure(error));
  }
}

function* getProfileWithoutDoc() {
  try {
    const { currentAccount: { brn = '' }, userInfo: { email = '' } } = yield select(getAuth);
    const result = yield call(apis.getProfileWithoutDoc, brn, email);
    yield put(actions.getProfileWithoutDoc.success(result.data));
  } catch (error) {
    yield put(actions.getProfileWithoutDoc.failure(error));
  }
}

function* updateProfile({ profileInfo }: any) {
  try {
    const { currentAccount, userInfo } = yield select(getAuth);
    const result = yield call(apis.updateProfile, {
      ...profileInfo,
      email: userInfo?.email,
      brn: currentAccount?.brn
    });
    yield put(actions.getProfileWithoutDoc.request());
    yield put(actions.updateProfile.success(result.data));
  } catch (error) {
    yield put(actions.updateProfile.failure(error));
  }
}

function* uploadDocuments({ newFiles, documentName, callback }: any) {
  try {
    const payload = yield convertAllFilesToDataUrl(newFiles, documentName);
    const formData = new FormData();
    const payloadData = payload.map((data) => {
      return {
        fileName: data.fileName,
        documentName: data.documentName
      }
    })
    newFiles.forEach(file => {
      formData.append("files", file);
    });
    formData.append("documentData", JSON.stringify(payloadData));
    //const result = yield call(apis.uploadDocuments, payload);
    const result = yield call(apis.uploadDocuments, formData);
    callback(result.data);
    yield put(actions.uploadDocuments.success());
  } catch (error) {
    yield put(actions.uploadDocuments.failure(error));
  }
}

function* deleteDocument({ deleteId, callback }: any) {
  try {
    const documentRepo = yield select(getDocumentRepo);
    const { [deleteId]: toDelete, ...newDocumentRepo } = documentRepo;
    const result = yield call(apis.deleteDocument, deleteId);
    callback(result.data);
    yield put(actions.deleteDocument.success({ documentRepo: newDocumentRepo }));
  } catch (error) {
    yield put(actions.deleteDocument.failure());
  }
}

function* downloadDocument({ documentId, callback }: any) {
  try {
    const documentRepo = yield select(getDocumentRepo);
    const { [documentId]: toDownloadBase64 } = documentRepo;

    if (toDownloadBase64) {
      callback(toDownloadBase64);
    } else {
      const result = yield call(apis.downloadDocument, documentId);
      let downloadData = {}
      if (result) {
        const objLink = URL.createObjectURL(result.data)
        const fileName = result.headers?.['filename'] || '';        // will come from api response header
        downloadData = {
          fileName: fileName,
          link: objLink
        }
      }
      callback(downloadData);
      yield put(actions.downloadDocument.success({ [documentId]: downloadData }));
    }
  } catch (error) {
    yield put(actions.downloadDocument.failure(error));
  }
}

function* fetchPolicyList({ payload }: any) {
  try {
    const {
      currentAccount: { msaheaderId, brn }
    } = yield select(getAuth);
    const result = yield call(apis.getPolicyList, {
      ...payload,
      msaheaderId,
      brn
    });
    yield put(actions.getPolicyList.success(result.data));
  } catch (error) {
    yield put(actions.getPolicyList.failure(error));
  }
}

function* fetchPolicyTemplate(currentAccount) {
  try {
    const { msaheaderId, brn } = currentAccount;
    const result = yield call(apis.getPolicyTemplate, {
      msaheaderId,
      brn
    });
    yield put(actions.getPolicyTemplate.success(result.data));
    return result.data.data.permissions;
  } catch (error) {
    yield put(actions.getPolicyTemplate.failure(error));
  }
}

function* fetchPolicyDetails({ options }: any) {
  try {
    const { currentAccount } = yield select(getAuth);
    let template = yield select(getPolicyTemplates);
    if (!template?.length) {
      template = yield call(fetchPolicyTemplate, currentAccount);
    }
    if (template?.length && options?.policyId) {
      const result = yield call(apis.getPolicyDetails, options);
      yield put(actions.getPolicyDetails.success(result.data));
    } else {
      yield put(actions.getPolicyDetails.failure({}));
    }
  } catch (error) {
    yield put(actions.getPolicyDetails.failure(error));
  }
}

function* savePolicy({ policy }: any) {
  try {
    const result = yield call(apis.updatePolicy, policy);
    yield put(actions.savePolicy.success(result.data));
  } catch (error) {
    yield put(actions.savePolicy.failure(error));
  }
}

function* deletePolicy({ policyId }: any) {
  try {
    const result = yield call(apis.deletePolicy, { policyId });
    yield put(actions.deletePolicy.success(result.data));
  } catch (error) {
    yield put(actions.deletePolicy.failure(error));
  }
}

function* getTabConfigType() {
  try {
    const result = yield call(apis.getTabConfigType);
    yield put(actions.getTabConfigType.success(result.data));
  } catch (error) {
    yield put(actions.getTabConfigType.failure(error));
  }
}
function* getAllOrderTranHistory({ page, size, orderNo, status, sortDir }: any) {
  try {
    const { userInfo: { email }, currentAccount: { brn } } = yield select(getAuth);
    const payload = { page, size, orderNo, searchTerm: orderNo, status, sortDir, sort: 'requestDate', brn, email }
    if (status === 'ALL' || status === 'In Progress') {
      delete payload.orderNo
    } else {
      delete payload.searchTerm
    }
    const result = yield call(apis.getAllOrderTranHistory, payload);
    yield put(actions.getAllOrderTranHistory.success(result.data));
  } catch (error) {
    yield put(actions.getAllOrderTranHistory.failure(error));
  }
}
function* getTransactionHistoryDetail(ordNo) {
  try {
    const {
      currentAccount: { brn },
      userInfo: { email }
    } = yield select(getAuth);
    const result = yield call(apis.getTransactionHistoryDetail, ordNo.ordNo, brn, email);
    yield put(actions.getTransactionHistoryDetail.success(result.data));
  } catch (error) {
    yield put(actions.getTransactionHistoryDetail.failure(error));
  }
}
function* getSpocApprovalStatusTranHistory(reqData) {
  try {
    const {
      currentAccount: { brn }
    } = yield select(getAuth);
    const result = yield call(apis.getApprovalStatusTranHistory, reqData.reqData);
    yield put(actions.getSpocApprovalStatusTranHistory.success(result.data));
    yield put(actions.getAllOrderTranHistory.request(1, 20, '', '', brn));
  } catch (error) {
    yield put(actions.getSpocApprovalStatusTranHistory.failure(error));
  }
}

function* getCancelTransOrderDetails(orderNo) {
  const payload = orderNo.orderNo;
  try {
    const result = yield call(apis.updateTransCancelStatus, payload);
    yield put(actions.getCancelTransOrder.success(result));
  } catch (error) {
    yield put(actions.getCancelTransOrder.failure(error));
  }
}

function* getUpdateDeliveryDetails(reqData) {
  const payload = reqData.reqData;
  try {
    const result = yield call(apis.updateDeliveryDetail, payload);
    yield put(actions.updateDeliveryDateDetails.success(result));
  } catch (error) {
    yield put(actions.updateDeliveryDateDetails.failure(error));
  }
}

function* getAllChangeUserDetails({ brn, page, size, searchRequest }: any) {
  try {
    const result = yield call(apis.getAllChangeUserDetails, brn, page, size, searchRequest);
    yield put(actions.getAllUserDetails.success(result));
  } catch (error) {
    yield put(actions.getAllUserDetails.failure(error));
  }
}

function* addNewUserData(reqData) {
  try {
    const result = yield call(apis.addNewUser, reqData.reqData);
    yield put(actions.addNewUserDetails.success(result));
  } catch (error) {
    yield put(actions.addNewUserDetails.failure(error));
  }
}

function* updateChangeUserDetails(reqData) {
  try {
    const {
      currentAccount: { brn }
    } = yield select(getAuth);
    const result = yield call(apis.updateChangeUser, reqData.updateInfo);
    yield put(actions.updateChangeUserDetails.success(result));
    yield put(actions.getAllUserDetails.request(brn, 1, 10, ''));
  } catch (error) {
    yield put(actions.updateChangeUserDetails.failure(error));
  }
}

function* fetchDeliveryAddressList(reqData) {
  try {
    const result = yield call(apis.getDeliveryAddressesList, { brn: reqData.brn });
    yield put(actions.getDeliveryAddressesList.success(result.data));
  } catch (error) {
    yield put(actions.getDeliveryAddressesList.failure(error));
  }
}
function* fetchResidentialAddressList(reqData) {
  try {
    const result = yield call(apis.getResidentialAddressesList, { brn: reqData.brn, masked: true });
    const unmaskedResult = yield call(apis.getResidentialAddressesList, { brn: reqData.brn, masked: false });
    const deliveryDetail = result.data.map((address, index) => {
      return {
        ...address,
        unmaskedRecpEmail: unmaskedResult.data[index].recpEmail,
        unmaskedRecpFirstName: unmaskedResult.data[index].recpFirstName,
        unmaskedRecpLastName: unmaskedResult.data[index].recpLastName,
        unmaskedRecpMobileNo: unmaskedResult.data[index].recpMobileNo
      }
    })
    yield put(actions.getResidentialAddressesList.success(deliveryDetail));
  } catch (error) {
    yield put(actions.getResidentialAddressesList.failure(error));
  }
}

function* saveDeliveryAddress(reqData) {
  try {
    const {
      currentAccount: { brn }
    } = yield select(getAuth);
    const result = yield call(apis.saveDeliveryAddressesList, reqData.payload);
    yield put(actions.saveDeliveryAddressesList.success(result.data));
    yield put(actions.getResidentialAddressesList.request(brn));
  } catch (error) {
    yield put(actions.saveDeliveryAddressesList.failure(error));
  }
}

function* editDeliveryAddress(reqData) {
  try {
    const {
      currentAccount: { brn }
    } = yield select(getAuth);
    const result = yield call(apis.editDeliveryAddressesList, reqData.payload);
    yield put(actions.editDeliveryAddressesList.success(result.data));
    yield put(actions.getResidentialAddressesList.request(brn));
  } catch (error) {
    yield put(actions.editDeliveryAddressesList.failure(error));
  }
}

function* deleteDeliveryAddress(reqData) {
  try {
    const {
      currentAccount: { brn }
    } = yield select(getAuth);
    const result = yield call(apis.deleteDeliveryAddresses, reqData.payload);
    yield put(actions.deleteDeliveryAddresses.success(result.data));
    yield put(actions.getResidentialAddressesList.request(brn));
  } catch (error) {
    yield put(actions.deleteDeliveryAddresses.failure(error));
  }
}

function* getAllBillingAccountDetails({ brn, page, size, searchParam }: any) {
  try {
    const result = yield call(apis.getBillingAccountsList, brn, page, size, searchParam);
    yield put(actions.getBillingAccountList.success(result.data));
  } catch (error) {
    yield put(actions.getBillingAccountList.failure(error));
  }
}

function* getBillingAccountEnrollmentStatus(reqData) {
  try {
    const result = yield call(apis.getBillingAccountEnrollmentStatus, reqData.reqData);
    yield put(actions.getBillingAccountEnrollmentStatus.success(result.data));
  } catch (error) {
    yield put(actions.getBillingAccountEnrollmentStatus.failure(error));
  }
}

function* getBillingAccountNumberList(reqData) {
  try {
    const result = yield call(apis.getBillingAccountNumber, reqData.reqData);
    yield put(actions.getServiceNumbersByBillingAccount.success(result.data));
  } catch (error) {
    yield put(actions.getServiceNumbersByBillingAccount.failure(error));
  }
}

function* updateCreditCardByBillingAccount(reqData) {
  try {
    const result = yield call(apis.updateCreditCardDetail, reqData.reqData);
    yield put(actions.updateCreditCardByBillingAccount.success(result.data));
    if (result.data?.redirectUrl) {
      window.location.replace(result.data.redirectUrl);
    }
  } catch (error) {
    yield put(actions.updateCreditCardByBillingAccount.failure(error));
  }
}

function* getChangedBillingAddress(reqData) {
  try {
    const result = yield call(apis.changeBillingAddress, reqData.reqData);
    yield put(actions.changeBillingAddress.success(result.data));
  } catch (error) {
    yield put(actions.changeBillingAddress.failure(error));
  }
}

function* uploadNumberPortDocuments({ newFiles, documentName, callback }: any) {
  try {
    const payload = yield convertAllFilesToDataUrl(newFiles, documentName);
    const formData = new FormData();
    const payloadData = payload.map((data) => {
      return {
        fileName: data.fileName,
        documentName: data.documentName
      }
    })
    newFiles.forEach(file => {
      formData.append("files", file);
    });

    formData.append("documentData", JSON.stringify(payloadData));
    const result = yield call(apis.uploadNumberPortDocuments, formData);

    callback(result?.data);
    yield put(actions.uploadNumberPortDocuments.success());
  } catch (error) {
    yield put(actions.uploadNumberPortDocuments.failure(error));
  }
}

function* downloadDocumentNumberPort({ documentId, callback }: any) {
  try {
    const documentRepo = yield select(getDocumentRepo);
    const { [documentId]: toDownloadBase64 } = documentRepo;

    if (toDownloadBase64) {
      callback(toDownloadBase64);
    } else {
      const result = yield call(apis.downloadDocumentNumberPort, documentId);
      let downloadData = {}
      if (result) {
        const objLink = URL.createObjectURL(result.data)
        const fileName = result.headers?.['filename'] || '';        // will come from api response header
        downloadData = {
          fileName: fileName,
          link: objLink
        }
      }
      callback(downloadData);
      yield put(actions.downloadDocumentNumberPort.success({ [documentId]: downloadData }));
    }
  } catch (error) {
    yield put(actions.downloadDocumentNumberPort.failure(error));
  }
}

function* deleteDocumentNumberPort({ deleteId, callback }: any) {
  try {
    const documentRepo = yield select(getDocumentRepo);
    const { [deleteId]: toDelete, ...newDocumentRepo } = documentRepo;
    const result = yield call(apis.deleteDocumentNumberPort, deleteId);
    callback(result.data);
    yield put(actions.deleteDocumentNumberPort.success({ documentRepo: newDocumentRepo }));
  } catch (error) {
    yield put(actions.deleteDocumentNumberPort.failure(error));
  }
}

export default function* accountSagas() {
  yield takeLatest(ACCOUNT_GET_PROFILE_REQUEST, getProfile);
  yield takeLatest(ACCOUNT_GET_PROFILE_WITHOUTDOC_REQUEST, getProfileWithoutDoc);
  yield takeEvery(ACCOUNT_UPDATE_PROFILE_REQUEST, updateProfile);
  yield takeLatest(ACCOUNT_UPLOAD_DOCUMENTS_REQUEST, uploadDocuments);
  yield takeLatest(ACCOUNT_DELETE_DOCUMENT_REQUEST, deleteDocument);
  yield takeLatest(ACCOUNT_GET_ACCOUNT_PAGE_REQUEST, getAccountPage);
  yield takeEvery(ACCOUNT_DOWNLOAD_DOCUMENT_REQUEST, downloadDocument);
  yield takeEvery(ACCOUNT_GET_POLICY_TEMPLATE_REQUEST, fetchPolicyTemplate);
  yield takeEvery(ACCOUNT_GET_POLICY_LSIT_REQUEST, fetchPolicyList);
  yield takeEvery(ACCOUNT_GET_POLICY_DETAILS_REQUEST, fetchPolicyDetails);
  yield takeEvery(ACCOUNT_SAVE_POLICY_REQUEST, savePolicy);
  yield takeEvery(ACCOUNT_DELETE_POLICY_REQUEST, deletePolicy);
  yield takeLatest(ACCOUNT_TABS_STATUS_REQUEST, getTabConfigType);
  yield takeLatest(ACCOUNT_TRANSACTION_HISTORY_REQUEST, getAllOrderTranHistory);
  yield takeLatest(ACCOUNT_TRANSACTION_HISTORY_SPOC_APPROVE_REQUEST, getSpocApprovalStatusTranHistory);
  yield takeLatest(ACCOUNT_TRANSACTION_HISTORY_CANCEL_REQUEST, getCancelTransOrderDetails);
  yield takeLatest(ACCOUNT_TRANSACTION_HISTORY_UPDATE_DELIVERY_REQUEST, getUpdateDeliveryDetails);
  yield takeLatest(ACCOUNT_GET_USER_DETAILS_REQUEST, getAllChangeUserDetails);
  yield takeLatest(ACCOUNT_ADD_NEW_USER_REQUEST, addNewUserData);
  yield takeLatest(ACCOUNT_UPDATE_CHANGE_USER_REQUEST, updateChangeUserDetails);
  yield takeLatest(ACCOUNT_GET_DELIVERY_ADDRESS_LIST_REQUEST, fetchDeliveryAddressList);
  yield takeLatest(ACCOUNT_GET_RESIDENTIAL_ADDRESS_LIST_REQUEST, fetchResidentialAddressList);
  yield takeLatest(ACCOUNT_SAVE_DELIVERY_ADDRESS_LIST_REQUEST, saveDeliveryAddress);
  yield takeLatest(ACCOUNT_EDIT_DELIVERY_ADDRESS_LIST_REQUEST, editDeliveryAddress);
  yield takeLatest(ACCOUNT_DELETE_DELIVERY_ADDRESS_REQUEST, deleteDeliveryAddress);
  yield takeLatest(ACCOUNT_BILLING_ACCOUNT_GET_LIST_REQUEST, getAllBillingAccountDetails);
  yield takeLatest(ACCOUNT_BILLING_ACCOUNT_GET_ENROLLMENT_STATUS_REQUEST, getBillingAccountEnrollmentStatus);
  yield takeLatest(ACCOUNT_BILLING_ACCOUNT_GET_SERVICE_NUMBER_LIST_REQUEST, getBillingAccountNumberList);
  yield takeLatest(BILLING_ACCOUNT_UPDATE_CREDIT_CARD_REQUEST, updateCreditCardByBillingAccount);
  yield takeLatest(ACCOUNT_TRANSACTION_HISTORY_DETAILS_REQUEST, getTransactionHistoryDetail);
  yield takeLatest(CHANGE_BILLING_ADDRESS_REQUEST, getChangedBillingAddress);
  yield takeLatest(ACCOUNT_UPLOAD_NUMBER_PORT_DOCUMENTS_REQUEST, uploadNumberPortDocuments);
  yield takeEvery(ACCOUNT_DOWNLOAD_DOCUMENT_NUMBERPORT_REQUEST, downloadDocumentNumberPort);
  yield takeLatest(ACCOUNT_DELETE_DOCUMENT_NUMBERPORT_REQUEST, deleteDocumentNumberPort);
}
